import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";

const STORAGE_KEY = "toulios.cookieConsent.v1";

/**
 * Cookie consent banner with granular toggle.
 * Three categories:
 *   - essential (always on, never disabled)
 *   - analytics (anonymised PostHog page views)
 *   - functional (e.g. AI Strategist session continuity)
 *
 * Decision is persisted to localStorage and never shown again
 * unless the user clears storage.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true,
    analytics: true,
    functional: true,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
    } catch {
      // localStorage may be blocked; show banner anyway, treat as session-only.
      setVisible(true);
    }
  }, []);

  const save = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...choice, savedAt: new Date().toISOString() })
      );
    } catch {
      // ignore storage failure
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl border border-gold/40 bg-[#0a0a0a]/95 backdrop-blur-md shadow-2xl">
        <div className="p-5 md:p-6">
          {!showDetails ? (
            <>
              <div className="flex items-start gap-3">
                <Cookie className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-display text-base text-white">
                    Cookies & analytics
                  </div>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    We use a small set of cookies. Essential cookies keep the
                    site working. Anonymised analytics help us understand which
                    pages get read. You choose. See our{" "}
                    <Link to="/trust" className="text-gold hover:underline">
                      Trust & Privacy
                    </Link>{" "}
                    page for the full breakdown.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  onClick={() => save({ essential: true, analytics: false, functional: false })}
                  className="btn-ghost text-xs"
                  data-testid="cookie-essential-only"
                >
                  Essential only
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="btn-outline-white text-xs"
                  data-testid="cookie-customise"
                >
                  Customise
                </button>
                <button
                  onClick={() => save({ essential: true, analytics: true, functional: true })}
                  className="btn-gold text-xs"
                  data-testid="cookie-accept-all"
                >
                  Accept all <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="font-display text-base text-white">
                  Cookie preferences
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-zinc-500 hover:text-white"
                  aria-label="Close cookie preferences"
                  data-testid="cookie-close-details"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 space-y-3">
                {[
                  {
                    key: "essential",
                    label: "Essential",
                    desc: "Required for the site to function (session, security). Cannot be disabled.",
                    locked: true,
                  },
                  {
                    key: "analytics",
                    label: "Analytics",
                    desc: "Anonymised page views via PostHog. No personal data sold or shared.",
                  },
                  {
                    key: "functional",
                    label: "Functional",
                    desc: "Remembers your preferences across sessions (e.g. AI Strategist context).",
                  },
                ].map((row) => (
                  <div
                    key={row.key}
                    className="flex items-start justify-between gap-4 border-l-2 border-white/[0.06] pl-4 py-1"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white">{row.label}</div>
                      <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{row.desc}</p>
                    </div>
                    <button
                      onClick={() => !row.locked && setPrefs((p) => ({ ...p, [row.key]: !p[row.key] }))}
                      disabled={row.locked}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                        prefs[row.key] ? "bg-gold" : "bg-zinc-700"
                      } ${row.locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      aria-pressed={prefs[row.key]}
                      data-testid={`cookie-toggle-${row.key}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-black transition-transform ${
                          prefs[row.key] ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn-ghost text-xs"
                >
                  Back
                </button>
                <button
                  onClick={() => save(prefs)}
                  className="btn-gold text-xs"
                  data-testid="cookie-save-preferences"
                >
                  Save preferences
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
